import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue } from "framer-motion";
import { REFERENCES } from "@/lib/data";
import ReferenceModal from "@/components/ReferenceModal";

const DUPS = 3;
const SCROLL_SPEED = 70; // px per second

function ReferenceCard({ image, name, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative mx-2 h-24 w-44 shrink-0 cursor-grab overflow-hidden rounded-xl border border-ink/5 bg-white shadow-sm outline-none transition-[transform,box-shadow] duration-300 hover:scale-105 hover:shadow-md focus-visible:ring-2 focus-visible:ring-orange/60 active:cursor-grabbing sm:mx-3 sm:h-28 sm:w-52"
      aria-label={`${name} referans detayı`}
    >
      <img
        src={image}
        alt={name}
        className="h-full w-full object-contain p-2 transition-transform duration-500 group-hover:scale-105"
        draggable={false}
      />
      {/* Shimmer overlay */}
      <div className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/70 to-transparent transition-transform duration-1000 ease-in-out group-hover:translate-x-full" />
    </button>
  );
}

export default function ReferenceSlider() {
  const [activeRef, setActiveRef] = useState(null);
  const [dragging, setDragging] = useState(false);
  const trackRef = useRef(null);
  const [singleWidth, setSingleWidth] = useState(0);
  const x = useMotionValue(0);
  const rafRef = useRef(null);
  const isDraggingRef = useRef(false);

  const items = Array(DUPS).fill(REFERENCES).flat();

  useEffect(() => {
    if (trackRef.current) {
      const width = trackRef.current.scrollWidth / DUPS;
      setSingleWidth(width);
    }
  }, []);

  useEffect(() => {
    if (!singleWidth) return;

    let lastTime = performance.now();
    let currentX = x.get();

    const loop = (now) => {
      if (isDraggingRef.current) {
        lastTime = now;
        currentX = x.get();
        rafRef.current = requestAnimationFrame(loop);
        return;
      }

      const dt = (now - lastTime) / 1000;
      currentX -= SCROLL_SPEED * dt;
      while (currentX <= -singleWidth) {
        currentX += singleWidth;
      }

      x.set(currentX);
      lastTime = now;
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [singleWidth, x]);

  const dragOffsetRef = useRef(0);

  const handleDrag = (_e, info) => {
    dragOffsetRef.current = Math.abs(info.offset.x);
    if (dragOffsetRef.current > 3) {
      isDraggingRef.current = true;
      setDragging(true);
    }
  };

  const handleDragStart = () => {
    dragOffsetRef.current = 0;
  };

  const handleDragEnd = () => {
    isDraggingRef.current = false;
    setTimeout(() => {
      setDragging(false);
      dragOffsetRef.current = 0;
    }, 80);
  };

  const handleCardClick = (ref) => {
    if (dragging || dragOffsetRef.current > 8) return;
    setActiveRef(ref);
  };

  return (
    <section className="relative -mt-8 overflow-hidden bg-cloud/50 pb-8 pt-6 backdrop-blur-sm sm:-mt-12 sm:pb-10 sm:pt-8">
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-cloud/50 to-transparent sm:w-24" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-cloud/50 to-transparent sm:w-24" />

      <motion.div
        ref={trackRef}
        style={{ x }}
        drag="x"
        dragConstraints={{
          left: -singleWidth * (DUPS - 1),
          right: 0,
        }}
        onDragStart={handleDragStart}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        className="flex"
      >
        {items.map((ref, idx) => (
          <ReferenceCard
            key={`${ref.name}-${idx}`}
            image={ref.image}
            name={ref.name}
            onClick={() => handleCardClick(ref)}
          />
        ))}
      </motion.div>

      <div className="mt-5 text-center">
        <span className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-[0.25em]">
          <span className="text-orange">Ref</span>
          <span className="text-lime">er</span>
          <span className="text-ink">ans</span>
          <span className="text-orange">la</span>
          <span className="text-lime">rı</span>
          <span className="text-ink">mız</span>
        </span>
      </div>

      <ReferenceModal item={activeRef} onClose={() => setActiveRef(null)} />
    </section>
  );
}
